export const API_BASE = "https://jobyab.online/api";

// Cloudflare Turnstile site key (public, safe to expose client-side).
export const TURNSTILE_SITE_KEY = "0x4AAAAAAEr-KXRq0PymcKtb";

export interface Job {
  id: number;
  title: string;
  company_id: number | null;
  location: string | null;
  source: string | null;
  remote_type: string | null;
  discovered_at: string | null;
  description: string | null;
  notes: string | null;
  application_status: string | null;
  interview_date: string | null;
  url: string | null;
  salary: string | null;
  estimated_salary: string | null;
  formatted_description: string | null;
  summary_bullets: string[] | null;
}

export interface Company {
  id: number;
  name: string;
  website: string | null;
  industry: string | null;
  notes: string | null;
  logo_url: string | null;
  description: string | null;
}

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string | null;
  job_id: number | null;
  is_read: boolean;
  created_at: string | null;
}

export interface MatchResult {
  id: number;
  job_id: number;
  match_score: number;
  recommend_apply: boolean;
  summary: string;
  strengths: string[];
  gaps: string[];
  red_flags: string[];
  status: string;
}

export type PrepLanguage = "de" | "en" | "fa";

export interface InterviewPrepQuestion {
  question: string;
  suggested_answer: string;
}

export interface InterviewPrep {
  id: number;
  job_id: number;
  language: PrepLanguage;
  questions: InterviewPrepQuestion[];
  elevator_pitch: string | null;
  company_notes: string[];
  created_at: string;
  updated_at: string | null;
}

// source values the backend stamps on jobs added through the manual import
// endpoints (as opposed to scraped sources).
export const MANUAL_URL_IMPORT_SOURCE = "manual-url-import";
export const MANUAL_TEXT_PASTE_SOURCE = "manual-text-paste";
export const MANUAL_SOURCES = [MANUAL_URL_IMPORT_SOURCE, MANUAL_TEXT_PASTE_SOURCE];

export async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  // Maintenance interception. The backend answers 503 on every endpoint while
  // maintenance mode is on, but it also answers 502/503 with a readable
  // `detail` when only an AI provider is down (see generateInterviewPrep) —
  // so a 503 is never trusted on its own: it is confirmed against the public
  // status endpoint first. That probe is a raw fetch (fetchSystemStatus) that
  // never passes back through this function, so it can't recurse. When
  // confirmed, the store latches and the MaintenanceGate in the root layout
  // swaps the whole app for the maintenance screen; the error thrown here just
  // covers this request's caller until that swap lands.
  if (res.status === 503 && (await confirmMaintenanceMode())) {
    throw new Error("JobYab is under maintenance right now. Please try again later.");
  }

  try {
    const data = await res.json();
    if (typeof data.detail === "string") return data.detail;
  } catch {
    // Body wasn't JSON (or was empty) — fall back to a generic message.
  }
  return fallback;
}

/* ------------------------------------------------------- maintenance mode */

export interface SystemStatus {
  maintenance_mode: boolean;
  // Optional human-readable message the backend may include while maintenance
  // mode is on; the maintenance screen shows it verbatim when present.
  message?: string | null;
}

// Confirmed maintenance state for the session. Latches ON when the status
// endpoint confirms a 503 (a confirmed latch never silently reverts — the next
// full page load re-checks) and flips OFF only when a forced recheck — the
// maintenance screen's retry button — sees the flag lifted.
let maintenanceActive = false;
let maintenanceMessage: string | null = null;
let maintenanceOffUntil = 0; // negative cache: a "not in maintenance" answer
let maintenanceInFlight: Promise<boolean> | null = null;
const maintenanceListeners = new Set<() => void>();

export function isMaintenanceMode(): boolean {
  return maintenanceActive;
}

export function getMaintenanceMessage(): string | null {
  return maintenanceMessage;
}

// The gate subscribes so a 503 confirmed by any request flips the mounted
// surface in the same tick the store latches, whichever request got there first.
export function subscribeMaintenance(listener: () => void): () => void {
  maintenanceListeners.add(listener);
  return () => {
    maintenanceListeners.delete(listener);
  };
}

function setMaintenance(active: boolean, message: string | null): void {
  const changed = maintenanceActive !== active;
  maintenanceActive = active;
  maintenanceMessage = active ? message : null;
  if (changed) {
    for (const listener of maintenanceListeners) listener();
  }
}

// Raw fetch on purpose: the confirmation probe must not run back through
// extractErrorMessage (which intercepts 503s) or it would recurse. The body is
// read even on a non-200 — /system/status stays public under maintenance and
// its body is the ground truth either way. Unreachable/unparsable → null:
// unknown, treated as NOT maintenance (an offline backend is not maintenance).
async function fetchSystemStatus(): Promise<SystemStatus | null> {
  try {
    const res = await fetch(`${API_BASE}/system/status`, { cache: "no-store" });
    return (await res.json()) as SystemStatus;
  } catch {
    return null;
  }
}

// Single-flight confirmation shared by the app-load check (MaintenanceGate)
// and the 503 interception above, with a short negative cache so an unrelated
// 503 (an AI-provider blip) doesn't turn into one probe per request.
export function confirmMaintenanceMode(): Promise<boolean> {
  if (maintenanceActive) return Promise.resolve(true);
  if (Date.now() < maintenanceOffUntil) return Promise.resolve(false);
  if (!maintenanceInFlight) {
    maintenanceInFlight = fetchSystemStatus()
      .then((status) => {
        const active = status?.maintenance_mode === true;
        setMaintenance(active, status?.message ?? null);
        return active;
      })
      .catch(() => false)
      .finally(() => {
        maintenanceOffUntil = Date.now() + 30_000;
        maintenanceInFlight = null;
      });
  }
  return maintenanceInFlight;
}

// Bypasses the negative cache — wired to the maintenance screen's retry button
// so lifting the flag clears the latch (and reveals the app) without a manual
// reload.
export function recheckMaintenanceMode(): Promise<boolean> {
  maintenanceOffUntil = 0;
  return confirmMaintenanceMode();
}

export async function analyzeJobMatch(jobId: number, token: string): Promise<MatchResult> {
  const res = await fetch(`${API_BASE}/matches/analyze/${jobId}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't analyze this job right now. Please try again.")
    );
  }

  return res.json();
}

export async function getMyMatches(token: string): Promise<MatchResult[]> {
  const res = await fetch(`${API_BASE}/matches/mine`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load your previous match analyses."));
  }

  return res.json();
}

export async function importJobFromUrl(url: string, token: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/import-url`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url }),
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't import a job from this link right now.")
    );
  }

  return res.json();
}

export async function importJobFromText(
  text: string,
  url: string | null,
  token: string
): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/import-text`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ text, url }),
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't import a job from this text right now.")
    );
  }

  return res.json();
}

export async function deleteJob(jobId: number, token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't delete this job right now."));
  }
}

export async function updateJobNotes(jobId: number, notes: string, token: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/notes`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ notes }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't save this note right now."));
  }

  return res.json();
}

export async function updateApplicationStatus(
  jobId: number,
  applicationStatus: string,
  token: string
): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/application-status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ application_status: applicationStatus }),
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't update the application status right now.")
    );
  }

  return res.json();
}

// interviewDate: ISO datetime string, or null to clear a scheduled interview.
export async function updateInterviewDate(
  jobId: number,
  interviewDate: string | null,
  token: string
): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/interview-date`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ interview_date: interviewDate }),
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't update the interview date right now.")
    );
  }

  return res.json();
}

export async function getJob(jobId: number, token: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load this job."));
  }

  return res.json();
}

// Creates (or regenerates) the backend-formatted description for a job whose
// formatted_description is still null, and returns the full updated Job.
// Auth required, like every agent endpoint. Note: unrelated to the local
// paragraphing heuristic in app/lib/format-description.ts, which shares a
// similar name — import sites alias one of the two.
export async function formatJobDescription(jobId: number, token: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/format-description`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't format this description right now.")
    );
  }

  return res.json();
}

// Generates (or regenerates) the short summary_bullets list for a job and
// returns the full updated Job. Auth required, like every agent endpoint.
export async function summarizeJobDescription(jobId: number, token: string): Promise<Job> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/summarize`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't summarize this description right now.")
    );
  }

  return res.json();
}

// Returns the saved prep for this job+language, or null when none exists (404).
export async function getInterviewPrep(
  jobId: number,
  language: PrepLanguage,
  token: string
): Promise<InterviewPrep | null> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/interview-prep?language=${language}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) return null;

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load your interview prep."));
  }

  const data = await res.json();
  return { ...data, questions: data.questions ?? [], company_notes: data.company_notes ?? [] };
}

// Generates (or regenerates) the prep — can take a few seconds. While the AI
// provider is down the backend answers 502/503 with a human-readable `detail`
// (usually Persian); extractErrorMessage surfaces it verbatim for the caller's
// error banner instead of a generic crash.
export async function generateInterviewPrep(
  jobId: number,
  language: PrepLanguage,
  token: string
): Promise<InterviewPrep> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/interview-prep?language=${language}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(
      await extractErrorMessage(res, "Couldn't generate interview prep right now. Please try again.")
    );
  }

  const data = await res.json();
  return { ...data, questions: data.questions ?? [], company_notes: data.company_notes ?? [] };
}

export async function getCompany(companyId: number, token: string): Promise<Company> {
  const res = await fetch(`${API_BASE}/companies/${companyId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load this company."));
  }

  return res.json();
}

export async function enrichCompany(companyId: number, token: string): Promise<Company> {
  const res = await fetch(`${API_BASE}/companies/${companyId}/enrich`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't enrich this company right now."));
  }

  return res.json();
}

export async function updateCompanyNotes(
  companyId: number,
  notes: string,
  token: string
): Promise<Company> {
  const res = await fetch(`${API_BASE}/companies/${companyId}/notes`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ notes }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't save this note right now."));
  }

  return res.json();
}

export async function createCompany(
  name: string,
  website: string | null,
  industry: string | null,
  token: string
): Promise<Company> {
  const res = await fetch(`${API_BASE}/companies`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name, website, industry }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't add this company right now."));
  }

  return res.json();
}

export interface GetNotificationsOptions {
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}

export async function getNotifications(
  token: string,
  options: GetNotificationsOptions = {}
): Promise<Notification[]> {
  const params = new URLSearchParams();
  if (options.unreadOnly) params.set("unread_only", "true");
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.offset !== undefined) params.set("offset", String(options.offset));

  const res = await fetch(`${API_BASE}/notifications?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load your notifications."));
  }

  return res.json();
}

export async function getUnreadCount(token: string): Promise<number> {
  const res = await fetch(`${API_BASE}/notifications/unread-count`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't check for new notifications."));
  }

  const data: { unread_count: number } = await res.json();
  return data.unread_count;
}

export async function markNotificationRead(id: number, token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/notifications/${id}/read`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't mark this notification as read."));
  }
}

export async function markAllNotificationsRead(token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/notifications/mark-all-read`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't mark all notifications as read."));
  }
}

/* ------------------------------------------------------------------ admin */

// Shapes mirror the backend's admin_* Pydantic models one-to-one (schemas.py).
// Every endpoint below is admin-gated server-side by get_current_admin_user,
// which answers 403 — that's why adminIsAuthorized returns null on 403: the
// /admin route uses it as its gate, since /auth/me carries no is_admin flag.

export interface AdminOverviewStats {
  total_users: number;
  users_last_7_days: number;
  users_last_30_days: number;
  total_jobs: number;
  total_companies: number;
  total_job_matches: number;
  digest_subscribers: number;
  verified_users: number;
  google_connected_users: number;
}

export interface AdminAIUsageModelRow {
  model: string | null;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface AdminAIUsageAgentRow {
  agent: string;
  calls: number;
  total_tokens: number;
}

export interface AdminAIUsageDaily {
  date: string;
  calls: number;
}

export interface AdminAIUsageStats {
  days: number;
  total_calls: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  by_model: AdminAIUsageModelRow[];
  by_agent: AdminAIUsageAgentRow[];
  daily_calls: AdminAIUsageDaily[];
  cost_estimation_available: boolean;
}

export interface AdminUserGrowthPoint {
  date: string;
  new_users: number;
}

export interface AdminUserGrowthStats {
  days: number;
  daily_new_users: AdminUserGrowthPoint[];
}

export interface AdminUserRow {
  id: number;
  email: string;
  full_name: string | null;
  created_at: string | null;
  email_verified: boolean;
  is_admin: boolean;
  is_active: boolean;
}

export interface AdminUserList {
  items: AdminUserRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface AdminUserDetail extends AdminUserRow {
  auth_provider: string;
  google_connected: boolean;
  google_email: string | null;
  job_count: number;
  application_count: number;
}

export interface AdminImpersonation {
  access_token: string;
  token_type: string;
  expires_in_seconds: number;
  impersonating_user: {
    id: number;
    email: string;
    full_name: string | null;
  };
}

export interface CreditTransaction {
  id: number;
  user_id: number;
  amount: number;
  reason: string;
  balance_after: number;
  admin_user_id: number | null;
  created_at: string | null;
}

export interface AdminCreditUserView {
  user_id: number;
  email: string;
  balance: number;
  free_monthly_allowance: number;
  recent_transactions: CreditTransaction[];
}

export interface AdminCreditGrantResult {
  user_id: number;
  amount: number;
  reason: string;
  balance: number;
}

export interface AdminTopConsumerRow {
  user_id: number;
  email: string;
  spent: number;
}

export interface AdminCreditsOverview {
  total_credits_held: number;
  total_spent_this_month: number;
  total_admin_granted_this_month: number;
  total_admin_deducted_this_month: number;
  monthly_allowance_granted_this_month: number;
  top_consumers_this_month: AdminTopConsumerRow[];
}

export interface MonitorLogRow {
  type: number | null;
  datetime: string | null;
  duration: number | null;
}

export interface MonitorStatus {
  id: number | null;
  name: string | null;
  url: string | null;
  status: string;
  status_code: number | null;
  uptime_24h: number | null;
  uptime_30d: number | null;
  average_response_time_ms: number | null;
  recent_response_times: number | null;
  logs: MonitorLogRow[];
}

export interface AdminInfraHealth {
  monitors: MonitorStatus[] | null;
  monitoring_error: string | null;
  monitoring_fetched_at: string | null;
  database: { postgres: "up" | "down" };
  disk: {
    path: string;
    total_bytes: number | null;
    used_bytes: number | null;
    free_bytes: number | null;
    percent_used: number | null;
  };
}

export interface AuditLogEntry {
  id: number;
  admin_user_id: number;
  admin_email: string | null;
  action: string;
  target_type: string | null;
  target_id: number | null;
  details: Record<string, unknown> | null;
  created_at: string | null;
}

export interface AdminAuditLogList {
  items: AuditLogEntry[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface FeatureFlag {
  key: string;
  value: string;
  description: string | null;
  updated_at: string | null;
  updated_by_admin_id: number | null;
  updated_by_admin_email: string | null;
}

// Shared paging params for the two paginated admin lists. Building the query
// string here (instead of at every call site) keeps ?search= escaping in one
// place — the backend matches with an unescaped LIKE under the hood.
export function adminUserListQuery(options: {
  page?: number;
  pageSize?: number;
  search?: string;
  isAdmin?: boolean | null;
  isActive?: boolean | null;
  emailVerified?: boolean | null;
}): string {
  const params = new URLSearchParams();
  if (options.page !== undefined) params.set("page", String(options.page));
  if (options.pageSize !== undefined) params.set("page_size", String(options.pageSize));
  if (options.search) params.set("search", options.search);
  if (options.isAdmin !== null && options.isAdmin !== undefined)
    params.set("is_admin", String(options.isAdmin));
  if (options.isActive !== null && options.isActive !== undefined)
    params.set("is_active", String(options.isActive));
  if (options.emailVerified !== null && options.emailVerified !== undefined)
    params.set("email_verified", String(options.emailVerified));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// The /admin gate. /auth/me has no is_admin field, so admin status can only be
// established by calling an admin endpoint: 200 → admin, 403 → not. Returns
// null on 403 (explicitly NOT admin, redirect), throws on any other failure so
// transient network errors don't lock a real admin out.
export async function adminIsAuthorized(token: string): Promise<boolean | null> {
  const res = await fetch(`${API_BASE}/admin/stats/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 403) return null;
  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't verify admin access."));
  }
  return true;
}

export async function getAdminOverviewStats(token: string): Promise<AdminOverviewStats> {
  const res = await fetch(`${API_BASE}/admin/stats/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load the overview stats."));
  }

  return res.json();
}

export async function getAdminAIUsage(
  days: number,
  token: string
): Promise<AdminAIUsageStats> {
  const res = await fetch(`${API_BASE}/admin/stats/ai-usage?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load AI usage stats."));
  }

  return res.json();
}

export async function getAdminUserGrowth(
  days: number,
  token: string
): Promise<AdminUserGrowthStats> {
  const res = await fetch(`${API_BASE}/admin/stats/user-growth?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load user growth stats."));
  }

  return res.json();
}

export async function getAdminUsers(
  options: Parameters<typeof adminUserListQuery>[0],
  token: string
): Promise<AdminUserList> {
  const res = await fetch(`${API_BASE}/admin/users${adminUserListQuery(options)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load the user list."));
  }

  return res.json();
}

export async function getAdminUser(
  userId: number,
  token: string
): Promise<AdminUserDetail> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load this user."));
  }

  return res.json();
}

export async function setAdminUserStatus(
  userId: number,
  isActive: boolean,
  token: string
): Promise<AdminUserRow> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_active: isActive }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't update the account status."));
  }

  return res.json();
}

export async function setAdminUserEmailVerified(
  userId: number,
  emailVerified: boolean,
  token: string
): Promise<AdminUserRow> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/email-verified`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email_verified: emailVerified }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't update email verification."));
  }

  return res.json();
}

export async function setAdminUserAdminStatus(
  userId: number,
  isAdmin: boolean,
  token: string
): Promise<AdminUserRow> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/admin-status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_admin: isAdmin }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't update the admin flag."));
  }

  return res.json();
}

export async function deleteAdminUser(
  userId: number,
  token: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't delete this user."));
  }
}

export async function forceLogoutAdminUser(
  userId: number,
  token: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/force-logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't force logout this user."));
  }
}

export async function impersonateAdminUser(
  userId: number,
  token: string
): Promise<AdminImpersonation> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/impersonate`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't start a view-as session."));
  }

  return res.json();
}

export async function getAdminUserCredits(
  userId: number,
  token: string
): Promise<AdminCreditUserView> {
  const res = await fetch(`${API_BASE}/admin/credits/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load this user's credits."));
  }

  return res.json();
}

export async function grantAdminUserCredits(
  userId: number,
  amount: number,
  reason: string,
  token: string
): Promise<AdminCreditGrantResult> {
  const res = await fetch(`${API_BASE}/admin/credits/${userId}/grant`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ amount, reason }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't adjust this user's credits."));
  }

  return res.json();
}

export async function getAdminCreditsOverview(
  token: string
): Promise<AdminCreditsOverview> {
  const res = await fetch(`${API_BASE}/admin/credits/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load the credits overview."));
  }

  return res.json();
}

export async function getAdminInfraHealth(token: string): Promise<AdminInfraHealth> {
  const res = await fetch(`${API_BASE}/admin/infra/health`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load infrastructure health."));
  }

  return res.json();
}

export async function getAdminAuditLog(
  options: {
    page?: number;
    pageSize?: number;
    action?: string;
    adminUserId?: number;
    targetType?: string;
  },
  token: string
): Promise<AdminAuditLogList> {
  const params = new URLSearchParams();
  if (options.page !== undefined) params.set("page", String(options.page));
  if (options.pageSize !== undefined) params.set("page_size", String(options.pageSize));
  if (options.action) params.set("action", options.action);
  if (options.adminUserId !== undefined)
    params.set("admin_user_id", String(options.adminUserId));
  if (options.targetType) params.set("target_type", options.targetType);
  const qs = params.toString();

  const res = await fetch(`${API_BASE}/admin/audit-log${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load the audit log."));
  }

  return res.json();
}

export async function getAdminFeatureFlags(token: string): Promise<FeatureFlag[]> {
  const res = await fetch(`${API_BASE}/admin/feature-flags`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't load feature flags."));
  }

  return res.json();
}

export async function setAdminFeatureFlag(
  key: string,
  value: string,
  token: string
): Promise<FeatureFlag> {
  const res = await fetch(`${API_BASE}/admin/feature-flags/${encodeURIComponent(key)}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ value }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res, "Couldn't update this feature flag."));
  }

  return res.json();
}
